import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { StatusCategory } from '@jira/platform__status-categories/src/status-categories.tsx';
import type {
	SCOPE_GLOBAL,
	SCOPE_PROJECT,
	ALL_OPERATION,
	ANY_OPERATION,
	ruleErrorCodes,
} from './workflowConstants.tsx';

type IssueTypeId = string;
type ProjectId = string;

export type RuleErrorCode = (typeof ruleErrorCodes)[keyof typeof ruleErrorCodes];

export type ScopeType = typeof SCOPE_GLOBAL | typeof SCOPE_PROJECT;

type StatusId = string;
type RuleId = string;
type RuleKey = string;

type TransitionId = string;

export type RuleConfig = {
	[key: string]: string;
};

export const ACTION_RULE_TYPE = 'action' as const;
export const VALIDATOR_RULE_TYPE = 'validator' as const;
export const TRIGGER_RULE_TYPE = 'trigger' as const;
export const CONDITION_RULE_TYPE = 'condition' as const;
export const SCREEN_RULE_TYPE = 'screen' as const;

export type RuleType =
	| typeof ACTION_RULE_TYPE
	| typeof VALIDATOR_RULE_TYPE
	| typeof TRIGGER_RULE_TYPE
	| typeof CONDITION_RULE_TYPE
	| typeof SCREEN_RULE_TYPE;

type AvailableRuleBase = {
	key: string;
	name: string;
	description: string;
	type: RuleType;
	icon: RuleIcon;
	isUnsupported: boolean;
	isVisible: boolean;
	isConfigurable: boolean;
	isConditional?: boolean;
	searchAliases?: string[];
	availableForInitial: boolean;
	incompatibleRules: string[];
};

export type ConfigurableSystemAvailableRule = AvailableRuleBase & {
	variety: 'system';
	isConfigurable: true;
	form: RuleForm;
};

type UnconfigurableSystemAvailableRule = AvailableRuleBase & {
	variety: 'system';
	isConfigurable: false;
};

export type SystemAvailableRule =
	| ConfigurableSystemAvailableRule
	| UnconfigurableSystemAvailableRule;

export type ConnectAvailableRule = AvailableRuleBase & {
	variety: 'connect';
	id: string;
	addonKey: string;
	moduleKey: string;
	appName?: string;
	appLogo?: string;
	createUrl?: string;
	editUrl?: string;
	viewUrl?: string;
};

export type ForgeAvailableRule = AvailableRuleBase & {
	variety: 'forge';
	id: string;
	appId: string;
	appName?: string;
	appLogo?: string;
};

export type AvailableRule = SystemAvailableRule | ConnectAvailableRule | ForgeAvailableRule;

export type RuleIcon = (props: { alt: string }) => React.JSX.Element;
type RuleForm = (props: BaseRuleProps) => React.JSX.Element;

type SupportedRuleConfig = {
	form?: RuleForm;
	icon: RuleIcon;
	searchAliases?: string[];
};

export type SupportedSystemRules = Record<string, SupportedRuleConfig>;

/**
 * @deprecated This type is incorrect, and should be { [string]: string }
 * Use StatusUiProperties if you expected an object value
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type StatusProperties = Record<string, any>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type StatusUiProperties = Record<string, any>;

/** Workflow Editor internal use only. This API may change without notice. */

export type Status = {
	id: StatusId;
	isNew: boolean;
	statusCategory: StatusCategory;
	name: string;
	description: string;
	scope: ScopeType;
	layout?: {
		x: number;
		y: number;
	};
	properties?: StatusProperties;
	uiProperties?: StatusUiProperties;
	approvalConfiguration?: CmpApprovalConfiguration;
	isMissingStatusPlaceholder?: boolean;
};

export type StatusRecords = Record<StatusId, Status>;

export type CmpApprovalConfiguration = {
	active?: string;
	conditionType?: string;
	conditionValue?: string;
	exclude?: string[];
	fieldId?: string;
	prePopulatedFieldId?: string;
	transitionApproved?: TransitionId;
	transitionRejected?: TransitionId;
};

/** Workflow Editor internal use only. This API may change without notice. */
export type Rule = {
	id: RuleId;
	displayText: string;
	config: RuleConfig;
	ruleKey: RuleKey;
	type?: RuleType;
};

export type ConditionGroupOperation = typeof ALL_OPERATION | typeof ANY_OPERATION;

export type ConditionGroup = {
	id: string;
	operation: ConditionGroupOperation;
	conditionGroups: ConditionGroup[];
	conditionIds: RuleId[];
};

export type TransitionEnum = 'INITIAL' | 'GLOBAL' | 'DIRECTED';

export type TransitionType = TransitionEnum;

/** Workflow Editor internal use only. This API may change without notice. */

export type TransitionProperties = Record<string, string>;

type TransitionLink = {
	fromRef: StatusId;
	fromPort?: number;
	toPort?: number;
};

export type Transition = {
	id: TransitionId;
	toRef?: StatusId;
	links: TransitionLink[];
	to?: never;
	from?: never;
	name: string;
	description: string;
	rules: Rule[];
	conditionTree?: ConditionGroup;
	type: TransitionType;
	properties?: TransitionProperties; // TODO: HIX-2155 Make required on ff tidy
	customIssueEventId?: string;
};

export type TransitionRecords = Record<TransitionId, Transition>;

export type Transitions = Transition[];

export type AssociatedEntityIds = {
	project: { id: ProjectId };
	issueTypes: IssueTypeId[];
}[];

export type Scope =
	| { type: typeof SCOPE_GLOBAL }
	| { type: typeof SCOPE_PROJECT; project: { id: ProjectId } };

export type MetaWorkflowVersionDocument = {
	id: string;
	name: string;
	description: string;
	versionNumber: number;
	versionId: string;
	scope: Scope;
	allStatuses: StatusRecords;
	visibleStatuses: {
		ref: StatusId;
	}[];
	transitions: TransitionRecords;
	startNodeLayout:
		| {
				x: number;
				y: number;
		  } // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<any, any>;
	loopedTransitionContainerLayout?: Partial<Coordinates>;
};

/*
    This is a representation of the workflow document as a "meta" entity.
    In other words, an object that does not follow any business logic of
    the existing workflow apps (editor, readonly view, history navigator).
    It serves as a convenient interface to expose the data to the apps, so they
    can then do their own transformations to the shape more convenient to their model.
*/

export type MetaWorkflowDocument = MetaWorkflowVersionDocument & {
	migrationId?: string;
	isEditable: boolean;
	hasDraft?: boolean;
};

export type MetaWorkflowDocumentWithUsages = MetaWorkflowDocument & {
	associatedIssueTypeIds: IssueTypeId[];
	associatedEntityIds: AssociatedEntityIds;
};

type ErrorSeverityEnum = 'WARNING' | 'ERROR';
export type ErrorNodeEnum = 'TRANSITION' | 'STATUS' | 'RULE' | 'WORKFLOW';

type ErrorTypeEnum =
	| 'CONFLICTING_NAME'
	| 'STATUS_ISLAND'
	| 'CUSTOM'
	| 'NON_DONE_SINK'
	| 'MISSING_FIELD'
	| 'MISSING_OPTION'
	| 'MISSING_ACCOUNT'
	| 'MISSING_ROLE'
	| 'MISSING_STATUS';

export type IssueType = {
	id: IssueTypeId;
	name: string;
	avatarId: number;
	iconUrl: string;
	hierarchyLevel: number;
};

export type TransitionWithFlag = Transition & {
	flag?: 'error' | 'warn';
};

export type RuleWithTransition = {
	transition: Transition;
} & Rule;

export type ProblemRulesByMessage = Map<
	string,
	{
		rule: RuleWithTransition;
		code?: RuleErrorCode;
		message?: string;
	}[]
>;

type ExtendedWorkflowError = WorkflowError & {
	elementId: string;
	ruleData?: RuleWithTransition;
	statusData?: Status;
	transitionData?: Transition;
};

export type AllValidationItems = ExtendedWorkflowError[];

export type Coordinates = {
	x: number;
	y: number;
};

export type StatusWithFlag = Status & {
	flag?: 'error';
};

export type DeprecatedStatus = {
	status: {
		ref: StatusId;
	};
	deprecatedBy: {
		ref?: StatusId;
	};
};

type WorkflowErrorCause = {
	key: string;
	value: string[];
};

export type WorkflowError = {
	type: ErrorTypeEnum | string;
	node: ErrorNodeEnum;
	severity: ErrorSeverityEnum;
	cause?: WorkflowErrorCause;
	code?: string;
	message?: string;
	additionalDetails?: string;
};

export type WorkflowErrorsMap = Record<StatusId | TransitionId | RuleId, WorkflowError[]>;

/** Layout information for Global Loops. Can be empty object.
 *
 * use toStartNodePosition to convert to Point or undefined for use internally in diagram
 */
export type StartNodeLayout = Partial<Coordinates>;

/** Layout information for Global Looped Transition Container. Can be empty object.
 *
 * use toGlobalLoopsContainerPosition to convert to Point or undefined for use internally in diagram
 */
export type GlobalLoopsContainerLayout = Partial<Coordinates> | undefined;

export type WorkflowDocumentState = {
	statuses: {
		allStatuses: StatusRecords;
		deprecatedStatuses: DeprecatedStatus[];
		visibleStatuses: {
			ref: StatusId;
		}[];
	};
	transitions: TransitionRecords;
	startNodeLayout:
		| {
				x: number;
				y: number;
		  } // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<any, any>;
	loopedTransitionContainerLayout: GlobalLoopsContainerLayout;
	isRovoCheckpoint?: boolean;
	changeMilestone?: string;
};

export type WorkflowDocumentUpdate = {
	previousWorkflowDocument: WorkflowDocumentState;
	currentWorkflowDocument: WorkflowDocumentState;
	migrationId?: string;
	issueTypes?: IssueType[];
};

export type IssueTypeDetails = {
	id: IssueTypeId;
	name: string;
	iconUrl: string;
	avatarId: number;
	hierarchyLevel: number;
};

export type ProjectDetails = {
	id: ProjectId;
	name: string;
	key: string;
	type?: ProjectType;
	avatarUrl?: string;
	issueTypes?: IssueTypeDetails[];
	isRestricted?: boolean;
	isTrashed?: boolean;
	isArchived?: boolean;
};

export type WorkflowDocumentMeta = {
	id: string;
	name: string;
	description: string;
	versionNumber: number;
	versionId: string;
	isEditable: boolean;
};

// Workflow capabilitites types

export const RULEDESCRIPTORTYPE_CONDITION = 'Condition';
export const RULEDESCRIPTORTYPE_VALIDATOR = 'Validator';
export const RULEDESCRIPTORTYPE_FUNCTION = 'Function';
export const RULEDESCRIPTORTYPE_SCREEN = 'Screen';

export type RuleDescriptorType = 'Condition' | 'Validator' | 'Function' | 'Screen';

type AvailableCapability = {
	capabilityKey: string;
	properties: Record<string, string>;
};

export type SystemAvailableRuleDTO = {
	ruleKey: string;
	ruleType: RuleDescriptorType;
	name?: string;
	description?: string;
	isVisible: boolean;
	isAvailableForInitialTransition: boolean;
	incompatibleRules: string[];
	isConditional?: boolean;
};

export type ConnectAvailableRuleDTO = {
	ruleKey: string;
	ruleType: RuleDescriptorType;
	addonKey: string;
	moduleKey: string;
	name: string;
	description?: string;
	createUrl?: string;
	editUrl?: string;
	viewUrl?: string;
	vendorInformation?: {
		addonName: string;
		vendorName: string;
		vendorLogo: string;
	};
	isConditional?: boolean;
};

export type ForgeAvailableRuleDTO = {
	ruleKey: string;
	ruleType: RuleDescriptorType;
	name: string;
	description: string;
	id: string;
	appId?: string; // remove as optional once hix-5920_expose_forge_meta_on_jws_capabilities_api is rolled out
	appName?: string;
	appLogo?: string;
	isConditional?: boolean;
};

export type TriggerAvailableRuleDTO = {
	type: string;
	name: string;
	description: string;
};

export type TriggersAvailableRuleDTO = {
	ruleKey: string;
	availableTypes: TriggerAvailableRuleDTO[];
};

export type WorkflowAllowedActions = {
	canEditRules: boolean;
	canManageEvents: boolean;
	canManageScreens: boolean;
	canNavigateToOldWorkflowEditor: boolean;
	statusManagement: {
		globalScoped: {
			add: boolean;
			edit: boolean;
			create: boolean;
		};
		projectScoped: {
			add: boolean;
			edit: boolean;
			create: boolean;
		};
	};
};

export type WorkflowCapabilities = {
	editorScope: ScopeType;
	projectTypes: ProjectType[];
	availableCapabilities: AvailableCapability[];
	systemRules: SystemAvailableRuleDTO[];
	connectRules: ConnectAvailableRuleDTO[];
	forgeRules: ForgeAvailableRuleDTO[];
	triggerRules: TriggersAvailableRuleDTO[];
	allowedActions: WorkflowAllowedActions;
	isDelegatedAdminMode: boolean;
};

export type BaseRuleProps = {
	/* The entities associated with the workflow */
	associatedEntityIds: AssociatedEntityIds;
	/* The issue type id the workflow is editing */
	issueTypeId: string;
	/* The project id the workflow is editing */
	projectId: string;
	/* The project key the workflow is editing */
	projectKey: string;
	/* The project type of the project the workflow is editing */
	projectType: ProjectType;
	/* The rule configuration, or parameters that defines the rule.
    ⚠️ This value changes on onRuleConfigChange which is not async safe. Please maintain local state of ruleConfig and use this prop as an initial value only
    See more https://hello.atlassian.net/browse/HIX-1847 */
	ruleConfig: RuleConfig;
	/* Getter for a copy of the workflow document exactly as returned by the server originally (without any client-side changes) */
	getBaseWorkflowDocument: () => WorkflowDocumentState;
	/* Getter for a copy of the CURRENT workflow document, with all client-side changes yet-to-be-saved by the user */
	getCurrentWorkflowDocument: () => WorkflowDocumentState;
	/* The workflow document meta data */
	workflowDocumentMeta: WorkflowDocumentMeta;
	/* Determines if the containing workflow is Company-Managed or Team-Managed */
	workflowScope: ScopeType;
	/** Determines all the capabilities of the workflow */
	workflowCapabilities: WorkflowCapabilities;
	/** Whether the selected rule supports conditional execution. */
	isConditional?: boolean;
	/**
	 * For passing up the rule configuration state from rule to rule editor.
	 * This is what will be saved if isValid is true and user clicks Save.
	 *
	 * @param {RuleConfig} ruleConfig rule configuration that will be saved if valid
	 * @param {boolean} isValid whether the rule configuration is valid and can be saved
	 * @param {AnalyticsAttributes} [analyticsMetaData] optional meta data to be passed on rule save
	 * */
	onRuleConfigChange: (
		ruleConfig: RuleConfig,
		isValid: boolean,
		ruleAnalyticsMetadata?: AnalyticsAttributes,
	) => void;
	/* The handler to indicate the rule is ready for user */
	onRuleLoaded: (isValid?: boolean) => void;
	/* The handler to delegate an error to the rules container in the workflow editor */
	onRuleError: (e: Error) => void;
	/* The handler to indicate whether another layer is open inside the rule form */
	onLayersChanged: (isOpen: boolean) => void;
};

export type APIMeta<T> = {
	isLoading?: boolean;
	data: T;
	error?: Error;
};

type ApiProjectId = string;
type ApiWorkflowId = string;
type ApiIssueTypeId = string;
type ApiStatusId = string;

export const INITIAL = 'INITIAL' as const;
export const GLOBAL = 'GLOBAL' as const;
export const DIRECTED = 'DIRECTED' as const;

type ApiTransitionType = typeof INITIAL | typeof GLOBAL | typeof DIRECTED;

type ApiProperties = {
	[key: string]: string;
};

export type ApiAssociation = {
	project: {
		id: ApiProjectId;
	};
	issueTypes: ApiIssueTypeId[];
};

export type ApiStatusRequest = {
	id?: ApiStatusId;
	name: string;
	description?: string;
	statusCategory: StatusCategory;
	statusReference: ApiStatusId;
};

export type ApiStatusResponse = {
	id: ApiStatusId;
	name: string;
	description: string;
	statusCategory: StatusCategory;
	statusReference: ApiStatusId;
	scope: Scope;
};

export type ApiStatusMeta = {
	statusReference: ApiStatusId;
	layout: {
		x?: number;
		y?: number;
	};
	properties?: {
		[key: string]: string;
	};
	approvalConfiguration?: ApiCmpApprovalConfiguration;
};

type ApiCmpApprovalConfiguration = {
	active?: string;
	conditionType?: string;
	conditionValue?: string;
	exclude?: string[];
	fieldId?: string;
	prePopulatedFieldId?: string;
	transitionApproved?: string;
	transitionRejected?: string;
};

export type ApiRule = {
	id: string;
	ruleKey: string;
	parameters: {
		[key: string]: string;
	};
	type?: RuleType;
};

export type ApiConditionGroup = {
	operation: 'ANY' | 'ALL';
	conditions: ApiRule[];
	conditionGroups: ApiConditionGroup[];
};

export type ApiTransitionLinks = {
	fromStatusReference?: ApiStatusId;
	fromPort?: number;
	toPort?: number;
}[];

export type ApiTransition = {
	from?: never;
	to?: never;
	toStatusReference?: string;
	links: ApiTransitionLinks;
	id: string;
	name: string;
	description: string;
	type: ApiTransitionType;
	actions: ApiRule[];
	validators: ApiRule[];
	triggers: ApiRule[];
	conditions?: ApiConditionGroup;
	transitionScreen?: ApiRule;
	properties?: ApiProperties;
	customIssueEventId?: string;
};

type ApiTransitions = ApiTransition[];

export type ApiWorkflowVersion = {
	id: ApiWorkflowId;
	name: string;
	description: string;
	version: {
		versionNumber: number;
		id: string;
	};
	scope: Scope;
	statuses: ApiStatusMeta[];
	transitions: ApiTransitions;
	startPointLayout?: {
		x: number;
		y: number;
	};
	loopedTransitionContainerLayout?: {
		x: number;
		y: number;
	};
};

export type ApiWorkflow = ApiWorkflowVersion & {
	migrationId?: string;
	isEditable: boolean;
	hasDraft?: boolean;
};

export type ApiWorkflowWithUsages = ApiWorkflow & {
	associatedWith: ApiAssociation[];
};

export type ApiGetWorkflowResponse = {
	statuses: ApiStatusResponse[];
	workflows: ApiWorkflow[];
};

export type ApiGetWorkflowVersionResponse = {
	statuses: ApiStatusResponse[];
	workflows: ApiWorkflowVersion[];
};

export type ApiGetWorkflowResponseWithUsages = {
	statuses: ApiStatusResponse[];
	workflows: ApiWorkflowWithUsages[];
};
